import { useState, useEffect, useCallback, useContext } from 'react';
import { RefreshCw, Plus, Pencil, ExternalLink, Monitor } from 'lucide-react';
import { getNativeClientPlatforms } from '@/services/native-client-platform.js';
import UserContext from '../../context/users';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';

export default function ClientsManager() {
    const [platforms, setPlatforms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentUser] = useContext(UserContext);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 8;

    const loadPlatforms = useCallback(async () => {
        if (!currentUser?.companyID) return;
        setLoading(true);
        try {
            const { data } = await getNativeClientPlatforms({ companyID: currentUser.companyID });
            setPlatforms(data);
        } catch (e) {
            console.error(e);
            toast.error('Falha ao carregar plataformas');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadPlatforms();
    }, [loadPlatforms]);

    const totalPages = Math.ceil(platforms.length / pageSize);
    const paginatedPlatforms = platforms.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <>
            {/* Actions bar */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                    {platforms.length} {platforms.length === 1 ? 'plataforma' : 'plataformas'}
                </p>
                <div className="flex gap-2">
                    <Button variant="ghost" size="sm" leftIcon={RefreshCw} onClick={loadPlatforms}>
                        Atualizar
                    </Button>
                    <Button variant="primary" size="sm" leftIcon={Plus} onClick={() => navigate('/client-platforms/new')}>
                        Nova Plataforma
                    </Button>
                </div>
            </div>

            {/* List */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex flex-col">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--dash-border)' }}>
                                    <div className="h-9 w-9 animate-pulse rounded-xl" style={{ background: 'var(--dash-surface-hover)' }} />
                                    <div className="flex flex-1 flex-col gap-1.5">
                                        <div className="h-3.5 w-32 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
                                        <div className="h-2.5 w-48 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : platforms.length === 0 ? (
                        <EmptyState
                            icon={Monitor}
                            title="Nenhuma plataforma"
                            description="Crie uma plataforma nativa para seus clientes."
                            action={
                                <Button variant="primary" size="sm" leftIcon={Plus} onClick={() => navigate('/client-platforms/new')}>
                                    Nova Plataforma
                                </Button>
                            }
                        />
                    ) : (
                        <div className="flex flex-col">
                            {paginatedPlatforms.map((item, index) => (
                                <div
                                    key={item.id}
                                    className={cn(
                                        'group flex items-center gap-3 px-4 py-3 transition-colors sm:px-5',
                                        index !== paginatedPlatforms.length - 1 && 'border-b'
                                    )}
                                    style={{ borderColor: 'var(--dash-border)' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--dash-surface-hover)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                    {/* Avatar */}
                                    {item.logo ? (
                                        <img
                                            src={item.logo}
                                            alt={item.name}
                                            className="h-9 w-9 rounded-xl object-cover"
                                        />
                                    ) : (
                                        <div
                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-semibold text-white"
                                            style={{ background: item.primaryColor || 'linear-gradient(135deg, #ad34fe 0%, #7b1fd4 100%)' }}
                                        >
                                            {item.name?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
                                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                            <button
                                                type="button"
                                                className="truncate text-left text-sm font-medium transition-colors"
                                                style={{ color: 'var(--dash-text)' }}
                                                onClick={() => navigate(`/client-platforms/${item.id}`)}
                                                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--dash-primary)')}
                                                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--dash-text)')}
                                            >
                                                {item.name}
                                            </button>
                                            <div className="flex items-center gap-2">
                                                <span className="truncate text-[11px]" style={{ color: 'var(--dash-text-faint)' }}>
                                                    {item.subdomain}.verboo.com
                                                </span>
                                                {item.customURL && (
                                                    <a
                                                        href={item.customURL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-0.5 text-[11px] transition-colors"
                                                        style={{ color: 'var(--dash-text-faint)' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--dash-primary)')}
                                                        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--dash-text-faint)')}
                                                    >
                                                        <ExternalLink size={10} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        <Badge variant={item.isActive ? 'success' : 'danger'} dot>
                                            {item.isActive ? 'Ativo' : 'Inativo'}
                                        </Badge>
                                    </div>

                                    {/* Edit */}
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/client-platforms/${item.id}`)}
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg opacity-0 transition-all duration-200 group-hover:opacity-100"
                                        style={{
                                            color: 'var(--dash-text-muted)',
                                            background: 'transparent',
                                            border: '1px solid transparent',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = 'var(--dash-primary)';
                                            e.currentTarget.style.background = 'var(--dash-primary-soft)';
                                            e.currentTarget.style.borderColor = 'var(--dash-primary-border)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = 'var(--dash-text-muted)';
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.borderColor = 'transparent';
                                        }}
                                    >
                                        <Pencil size={13} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-all duration-200"
                            style={{
                                background: page === currentPage ? 'var(--dash-primary-soft)' : 'transparent',
                                color: page === currentPage ? 'var(--dash-primary)' : 'var(--dash-text-muted)',
                                border: page === currentPage ? '1px solid var(--dash-primary-border)' : '1px solid transparent',
                            }}
                        >
                            {page}
                        </button>
                    ))}
                </div>
            )}
        </>
    );
}
